/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import IcomoonReact from 'icomoon-react';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import SelectDropdownExercisePicker from './SelectDropdownExercisePicker';
import Text from '../../../../shared/components/Text/Text';

const ExerciseSelectorContainer = styled('div')`
	display: flex;
  width: 100%;
  margin-top: 10px;
  margin-bottom: 20px;
  padding-left: ${(props) => props.addPaddingLeft && '25px'};
`;

const AddExerciseButtonContainer = styled('div')`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-right: 5px;
  
  &:focus {
    outline: 2px solid #77A7FF;
    outline-offset: 2px;
  }
`;

const BorderContainer = styled('div')`
  position: relative;
  padding-right: 13px;
  align-items: center;
  display: flex;
  margin-left: ${(props) => {
    if (props.isTvMode) return props.isWeekView ? '10px' : '2px';
    if (!props.calendarItemsHaveLength && props.viewType === 'dayView' && !props.hasAfter && !props.hasBefore && props.programType === 'free-form-program') return '0';
    return props.isWeekView ? '10px' : '28px';
  }};
  &::before {
    content: '';
    position: absolute;
    right: 1px;
    top: 50%;
    transform: translateY(-50%);
    height: 21px; /* Set the height of the border here */
    width: 2px;
    background-color: #444444; /* Border color */
  }
`;

const ButtonIcon = styled('div')`
  padding: 4px;
  background: #444444;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: ${(props) => (props.isTvMode ? '35px' : '25px')};
  min-width: ${(props) => (props.isTvMode ? '35px' : '25px')};
  max-width: ${(props) => (props.isTvMode ? '35px' : '25px')};
  max-height: ${(props) => (props.isTvMode ? '35px' : '25px')};
`;

const ExercisePicker = ({
  isWeekView, calendarItemsHaveLength, hasBefore, hasAfter, date, expandDay, dayNumber,
}) => {
  const [addExerciseToggleMenuOpen, setAddExerciseToggleMenuOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const programType = useSelector((state) => state.calendar.data.programType);
  const isTvMode = useSelector((state) => state.calendar.ui.isTvMode);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const effectiveDate = isWeekView ? date : selectedDate;
  const endpointFormattedDate = moment(effectiveDate).format('YYYY-MM-DD');

  const currentDateOrDayNumber = programType === 'free-form-program' ? dayNumber : endpointFormattedDate;
  const isThisDayHorizontallyExpanded = useSelector(
    (state) => String(state.calendar.data.weekViewExpandedDay) === String(currentDateOrDayNumber),
  );

  const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);

  // Use a high tabIndex to ensure ExercisePicker comes after all exercise inputs and AddSet fields
  const tabIndex = 9999;

  const handleFocus = () => {
    // Automatically open dropdown when focused via keyboard
    if (isWeekView && expandDay && !isThisDayHorizontallyExpanded) {
      expandDay();
      setTimeout(() => {
        setAddExerciseToggleMenuOpen(true);
      }, 600);
    } else {
      setAddExerciseToggleMenuOpen(true);
    }
  };

  const handleClick = () => {
    if (isWeekView && expandDay && !isThisDayHorizontallyExpanded) {
      expandDay();
      setTimeout(() => {
        setAddExerciseToggleMenuOpen(!addExerciseToggleMenuOpen);
      }, 600);
    } else {
      setAddExerciseToggleMenuOpen(!addExerciseToggleMenuOpen);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isWeekView && !isThisDayHorizontallyExpanded) return;

      const tag = e.target.tagName;
      if ((tag === 'INPUT' && e.target.type !== 'checkbox')
        || tag === 'TEXTAREA'
        || e.target.isContentEditable) {
        return;
      }

      const isCtrlN = isMac && e.ctrlKey && e.key.toLowerCase() === 'n';
      const isAltN = !isMac && e.altKey && e.key.toLowerCase() === 'n';
      const isShiftSpace = e.shiftKey && e.key === ' ';

      if (isCtrlN || isAltN || isShiftSpace) {
        e.preventDefault();
        setAddExerciseToggleMenuOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isThisDayHorizontallyExpanded, isWeekView, isMac]);

  return (
    <ExerciseSelectorContainer
      hasAfter={hasAfter}
      addPaddingLeft={((isWeekView
      && isThisDayHorizontallyExpanded))}
    >
      <AddExerciseButtonContainer
        onMouseDown={handleClick}
        onFocus={handleFocus}
        tabIndex={tabIndex}
      >
        <BorderContainer
          isWeekView={isWeekView}
          calendarItemsHaveLength={calendarItemsHaveLength}
          viewType={viewType}
          hasAfter={hasAfter}
          hasBefore={hasBefore}
          programType={programType}
          isTvMode={isTvMode}
        >
          <ButtonIcon isTvMode={isTvMode}>
            <IcomoonReact
              iconSet={iconSet}
              size={isTvMode ? 20 : 12}
              icon='plus'
              color='white'
            />
          </ButtonIcon>
        </BorderContainer>
      </AddExerciseButtonContainer>
      {!addExerciseToggleMenuOpen && (
        <AddExerciseButtonContainer onClick={handleClick} tabIndex={-1}>
          {(viewType === 'dayView' || (viewType === 'weekView' && isThisDayHorizontallyExpanded)) ? (
            <Text style={{
              color: 'hsl(0, 0%, 50%)',
              whiteSpace: 'nowrap',
              marginLeft: 2,
              marginRight: 2,
            }}
            >
              {isMac
                ? 'Add Exercise (Ctrl+N)'
                : 'Add Exercise (Alt+N)'}
            </Text>
          ) : (
            <Text style={{
              color: 'hsl(0, 0%, 50%)',
              whiteSpace: 'nowrap',
              marginLeft: 2,
              marginRight: 2,
            }}
            >
              Add Exercise
            </Text>
          ) }
        </AddExerciseButtonContainer>
      )}
      <SelectDropdownExercisePicker
        fullWidthInput
        placeholder='Search Exercises'
        addExercisePicker
        addExerciseToggleMenuOpen={addExerciseToggleMenuOpen}
        setAddExerciseToggleMenuOpen={setAddExerciseToggleMenuOpen}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        tabIndex={-1}
        date={date}
        dayNumber={dayNumber}
        isWeekView={isWeekView}
        expandDay={expandDay}
      />
    </ExerciseSelectorContainer>
  );
};

ExercisePicker.defaultProps = {
  isWeekView: false,
  calendarItemsHaveLength: false,
  hasBefore: false,
  hasAfter: false,
  date: null,
  dayNumber: null,
  expandDay: () => {},
};

ExercisePicker.propTypes = {
  isWeekView: PropTypes.bool,
  calendarItemsHaveLength: PropTypes.bool,
  hasBefore: PropTypes.bool,
  hasAfter: PropTypes.bool,
  date: PropTypes.string,
  dayNumber: PropTypes.number,
  expandDay: PropTypes.func,
};

export default ExercisePicker;
